import { Component, EventEmitter, Input, Output } from '@angular/core';

type ActionOption = 'images-sending' | 'images-retrieving' | 'documents-sending' | 'documents-retrieving' | 'patient-library';
type Category = 'images' | 'documents' | 'patient-library';

@Component({
    selector: 'app-patients-actions-buttons',
    templateUrl: './patients-actions-buttons.component.html',
    styleUrls: ['./patients-actions-buttons.component.scss'],
    standalone: false
})
export class PatientsActionsButtonsComponent {
  @Input() selectedOption: ActionOption | null = null;

  @Output() optionToggled = new EventEmitter<{ option: ActionOption; checked: boolean }>();

  expandedCategory: Category | null = null;

  get selectedCategory(): Category | null {
    if (!this.selectedOption) return null;
    if (this.selectedOption === 'images-sending' || this.selectedOption === 'images-retrieving') {
      return 'images';
    }
    if (this.selectedOption === 'documents-sending' || this.selectedOption === 'documents-retrieving') {
      return 'documents';
    }
    return null;
  }

  get isImagesExpanded(): boolean {
    return this.expandedCategory === 'images';
  }

  get isDocumentsExpanded(): boolean {
    return this.expandedCategory === 'documents';
  }

  get selectedOptionLabel(): string | null {
    if (!this.selectedOption) return null;
    switch (this.selectedOption) {
      case 'images-sending':
        return 'Send';
      case 'images-retrieving':
        return 'Retrieve';
      case 'documents-sending':
        return 'Send';
      case 'documents-retrieving':
        return 'Retrieve';
      default:
        return null;
    }
  }

  get imagesSelectionLabel(): string | null {
    if (this.selectedOption === 'images-sending') {
      return 'Send';
    }
    if (this.selectedOption === 'images-retrieving') {
      return 'Retrieve';
    }
    return null;
  }

  get documentsSelectionLabel(): string | null {
    if (this.selectedOption === 'documents-sending') {
      return 'Send';
    }
    if (this.selectedOption === 'documents-retrieving') {
      return 'Retrieve';
    }
    return null;
  }

  getCategoryIcon(category: Category): string {
    switch (category) {
      case 'images':
        return 'image';
      case 'documents':
        return 'description';
      case 'patient-library':
        return 'folder';
      default:
        return '';
    }
  }

  onCategoryClick(category: Category): void {
    if (category === 'patient-library') return; // Disabled
    
    if (category === 'images') {
      // If switching from Documents, deselect it first
      if (this.selectedCategory === 'documents' && 
          (this.selectedOption === 'documents-sending' || this.selectedOption === 'documents-retrieving')) {
        this.optionToggled.emit({ option: this.selectedOption, checked: false });
      }
      
      // Collapse Documents dropdown if it's expanded
      if (this.expandedCategory === 'documents') {
        this.expandedCategory = null;
      }
      
      // Toggle expansion for Images category
      if (this.expandedCategory === 'images') {
        this.expandedCategory = null;
        // If an image option was selected, deselect it
        if (this.selectedOption === 'images-sending' || this.selectedOption === 'images-retrieving') {
          this.optionToggled.emit({ option: this.selectedOption, checked: false });
        }
      } else {
        this.expandedCategory = 'images';
      }
    } else if (category === 'documents') {
      // If switching from Images, deselect it first
      if (this.selectedCategory === 'images' && 
          (this.selectedOption === 'images-sending' || this.selectedOption === 'images-retrieving')) {
        this.optionToggled.emit({ option: this.selectedOption, checked: false });
      }
      
      // Collapse Images dropdown if it's expanded
      if (this.expandedCategory === 'images') {
        this.expandedCategory = null;
      }
      
      // Toggle expansion for Documents category
      if (this.expandedCategory === 'documents') {
        this.expandedCategory = null;
        // If a document option was selected, deselect it
        if (this.selectedOption === 'documents-sending' || this.selectedOption === 'documents-retrieving') {
          this.optionToggled.emit({ option: this.selectedOption, checked: false });
        }
      } else {
        this.expandedCategory = 'documents';
      }
    }
  }

  onSubOptionToggle(option: ActionOption, checked: boolean): void {
    // If unchecking, just emit the event
    if (!checked) {
      this.optionToggled.emit({ option, checked });
      return;
    }

    // Handle image options
    if (option === 'images-sending' || option === 'images-retrieving') {
      const currentImageOption = this.selectedOption === 'images-sending' || this.selectedOption === 'images-retrieving' 
        ? this.selectedOption 
        : null;
      
      // If a different image option is currently selected, deselect it first
      if (currentImageOption && currentImageOption !== option) {
        this.optionToggled.emit({ option: currentImageOption, checked: false });
      }
      
      // Select the new option
      this.optionToggled.emit({ option, checked: true });
      
      // Collapse the dropdown after selection
      this.expandedCategory = null;
    } 
    // Handle document options - both should open clientprofile
    else if (option === 'documents-sending' || option === 'documents-retrieving') {
      const currentDocumentOption = this.selectedOption === 'documents-sending' || this.selectedOption === 'documents-retrieving' 
        ? this.selectedOption 
        : null;
      
      // If a different document option is currently selected, deselect it first
      if (currentDocumentOption && currentDocumentOption !== option) {
        this.optionToggled.emit({ option: currentDocumentOption, checked: false });
      }
      
      // Select the new option
      this.optionToggled.emit({ option, checked: true });
      
      // Collapse the dropdown after selection
      this.expandedCategory = null;
    } else {
      this.optionToggled.emit({ option, checked });
    }
  }
}
